import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Banner from '../components/Banner';
import SearchResults from '../components/SearchResults';
import FormComponent from '../components/FormComponent';
import TableComponent from '../components/TableComponent';
import LatestRegisteredCompanies from '../components/LatestRegisteredCompanies';
import KosovoMap from '../components/KosovoMap';
import Features from '../components/Features';
import Pricing from '../components/Pricing';
import Footer from '../components/Footer';
import WorkProcess from '../components/WorkProcess';
import { useAuth } from '../context/AuthContext';

import { getLatestBusinesses } from '../api/latestBusinesses';
import { filterByCheckboxServices } from '../api/filterByCheckBoxServices';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';

const Home = () => {
    const { authStateChanged } = useAuth();
    const [businesses, setBusinesses] = useState([]);
    const [statisticsData, setStatisticsData] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [latestCompanies, setLatestCompanies] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [filterOptionsState, setFilterOptionsState] = useState({
        municipalities: [],
        activities: [],
        sectors: [],
        statuses: [],
        businessTypes: [],
        businessNames: [],
        capitalRanges: [],
        uniqueIds: [],
        registrationDates: [],
        genders: [],
    });

    useEffect(() => {
        fetchStatisticsData();
        fetchLatestCompanies();
        fetchFilterOptions();
    }, [authStateChanged]);

    const fetchStatisticsData = async () => {
        try {
            const response = await api.get('/api/business-counts/');
            setStatisticsData(response.data);
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    };

    const fetchLatestCompanies = async () => {
        try {
            const data = await getLatestBusinesses();
            console.log('Latest businesses data:', data);
            if (Array.isArray(data)) {
                setLatestCompanies(data);
            } else if (data && Array.isArray(data.businesses)) {
                setLatestCompanies(data.businesses);
            } else {
                console.warn('Unexpected latest businesses data format:', data);
                setLatestCompanies([]);
            }
        } catch (error) {
            console.error('Error fetching latest companies:', error);
            setLatestCompanies([]);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const response = await api.get('/api/filter-options/');
            const data = response.data;
            console.log('Filter options response:', data);
            
            setFilterOptionsState({
                municipalities: data.municipalities || [],
                activities: data.activities || [], 
                sectors: data.sectors?.map(item => ({
                    value: item.sector_id,
                    label: item.name
                })) || [],
                statuses: data.status_in_arbk?.map(item => ({
                    value: item,
                    label: item
                })) || [],
                businessTypes: data.business_types?.map(item => ({
                    value: item,
                    label: item
                })) || [],
                businessNames: data.business_names || [],
                capitalRanges: data.capital_ranges || [],
                uniqueIds: data.unique_identifiers || [],
                registrationDates: data.registration_dates || [],
                genders: data.genders?.map(item => ({
                    value: item,
                    label: item === 'Male' ? 'Mashkull' : item === 'Female' ? 'Femër' : 'Unknown'
                })) || []
            });
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const handleSearchResults = (results) => {
        console.log('Search results received:', results);
        if (!results) {
            console.warn('Search results are undefined');
            setSearchResults([]);
            setShowResults(false);
            return;
        }
        setSearchResults(Array.isArray(results) ? results : [results]);
        setShowResults(true);
        setCompanies([]); 

        setTimeout(() => {
            const resultsElement = document.getElementById('searchResults');
            if (resultsElement) {
                resultsElement.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const handleFilterSubmit = async (filters) => {
        try {
            const response = await filterByCheckboxServices(filters);
            if (response && response.businesses) {

                const last10Companies = response.businesses.slice(-10).reverse();
                setCompanies(last10Companies);
                setSearchResults([]);
                setShowResults(true);
            }
        } catch (error) {
            console.error("Error fetching filtered data:", error);
        }
    };

    return (
        <div className="home-container">
            <Header />
            <Banner statisticsData={statisticsData} onSearchResults={handleSearchResults} />
            
            <AnimatePresence>
                {searchResults.length > 0 && (
                    <motion.div
                        id="searchResults"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
                        className="tw-container tw-mx-auto tw-px-4 tw-py-8"
                    >
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6">
                            <h2 className="tw-text-xl tw-font-semibold tw-mb-4">
                                Search Results ({searchResults.length} {searchResults.length === 1 ? 'business' : 'businesses'} found)
                            </h2>
                            <TableComponent 
                                companies={searchResults}
                                simplified={false}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <FormComponent
                municipalities={filterOptionsState.municipalities}
                activities={filterOptionsState.activities}
                sectors={filterOptionsState.sectors} 
                statuses={filterOptionsState.statuses}
                businessTypes={filterOptionsState.businessTypes}
                businessNames={filterOptionsState.businessNames}
                capitalRanges={filterOptionsState.capitalRanges}
                uniqueIds={filterOptionsState.uniqueIds}
                registrationDates={filterOptionsState.registrationDates}
                genders={filterOptionsState.genders}
                onSubmit={handleFilterSubmit}
            />

            {companies.length > 0 && (
                <div className="tw-container tw-mx-auto tw-px-4 tw-py-8">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6">
                        <h2 className="tw-text-xl tw-font-semibold tw-mb-4">
                            Filter Results ({companies.length} {companies.length === 1 ? 'business' : 'businesses'} found)
                        </h2>
                        <TableComponent businesses={[]} companies={companies} simplified={true} />
                    </div>
                </div>
            )}

            <LatestRegisteredCompanies companies={latestCompanies} />
            <KosovoMap />
            <Features />
            <WorkProcess />
            <Pricing />
            <Footer />
        </div>
    );
};

export default Home;
